import { get } from '@shared/adapters/lodash'
import { formatDisplayDate, formatDisplayTime } from '@shared/utils/datetimeUtils'
import type {
  TransactionApiRow,
  TransactionPaginationDto,
  TransactionsApiResponseDto,
  TransactionDetailWireDto,
} from './transactionContracts'
import type {
  RentalPayout,
  Transaction,
  TransactionStatus,
  TransactionType,
} from '../types/transactionTypes'
import type {
  TransactionDetail,
  TransactionDepositDetails,
  TransactionInvestmentDetails,
  TransactionRentalDetails,
  TransactionWithdrawalDetails,
} from '../types/transactionDetailTypes'

const TYPE_MAP: Record<string, TransactionType> = {
  deposit: 'deposit',
  withdrawal: 'withdrawal',
  investment: 'investment',
  rental_income: 'rental_income',
  rental: 'rental',
  refund: 'refund',
  token_received: 'token_received',
  secondary_purchase: 'secondary_purchase',
  secondary_sale_proceeds: 'secondary_sale_proceeds',
}

const STATUS_MAP: Record<string, TransactionStatus> = {
  completed: 'completed',
  pending: 'pending',
  processing: 'processing',
  failed: 'failed',
  reversed: 'reversed',
}

const CATEGORY_LABEL: Record<TransactionType, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  investment: 'Investment',
  rental_income: 'Rental Income',
  rental: 'Rental',
  refund: 'Refund',
  token_received: 'Received',
  secondary_purchase: 'Secondary Purchase',
  secondary_sale_proceeds: 'Sale Proceeds',
}

function formatAmount(row: TransactionApiRow): string {
  const raw = parseFloat(row.amount)
  if (isNaN(raw)) return row.amount

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(raw))

  // Both directions carry a sign — an unsigned debit reads like income on a
  // ledger row, which is exactly the money-honesty problem to avoid.
  const prefix = row.transactionType === 'credit' ? '+' : '-'
  return `${prefix}${row.currencyCode} ${formatted}`
}

export function parseTransactionRow(row: TransactionApiRow): Transaction {
  const type: TransactionType = TYPE_MAP[row.transactionReason] ?? 'deposit'
  const status: TransactionStatus = STATUS_MAP[row.status] ?? 'pending'
  const date = row.createdAt ? new Date(row.createdAt) : new Date()

  return {
    id: row.id,
    type,
    title: row.displayTitle,
    category: CATEGORY_LABEL[type],
    date: formatDisplayDate(date),
    time: formatDisplayTime(date),
    createdAt: row.createdAt ?? '',
    referenceId: row.referenceId ?? null,
    amount: formatAmount(row),
    incoming: row.transactionType === 'credit',
    status,
    // Rental rows name their property under rentalDetails; fall back to the
    // flat field the other legs use.
    propertyName: row.rentalDetails?.propertyName ?? row.propertyName,
    rentalPeriod: row.rentalDetails?.distributionPeriod ?? null,
  }
}

export function parseRentalPayoutRow(row: TransactionApiRow): RentalPayout {
  const amount = parseFloat(row.amount)
  return {
    id: row.id,
    amount: Number.isFinite(amount) ? Math.abs(amount) : 0,
    currency: row.currencyCode || 'AED',
    status: STATUS_MAP[row.status] ?? 'pending',
    propertyName: row.propertyName ?? null,
    createdAt: row.createdAt,
  }
}

export interface ParsedTransactionsPage {
  rows: Transaction[]
  pagination: TransactionPaginationDto
}

export function parseTransactionsResponse(resp: unknown): ParsedTransactionsPage {
  const dto = (resp ?? {}) as TransactionsApiResponseDto
  const rows = (dto.data ?? []) as TransactionApiRow[]
  const pagination: TransactionPaginationDto = dto.pagination ?? {
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
  }
  return {
    rows: rows.map(parseTransactionRow),
    pagination,
  }
}

const toWithdrawal = (
  raw: TransactionDetailWireDto['withdrawalDetails'],
): TransactionWithdrawalDetails | null => {
  if (!raw || typeof raw !== 'object') return null
  return {
    bankName: raw.bankName ?? null,
    maskedAccount: raw.maskedAccount ?? null,
    bankReferenceId: raw.bankReferenceId ?? null,
    bankStatus: raw.bankStatus ?? null,
  }
}

const toInvestment = (
  raw: TransactionDetailWireDto['investmentDetails'],
): TransactionInvestmentDetails | null => {
  if (!raw || typeof raw !== 'object') return null
  return {
    propertyName: raw.propertyName ?? null,
    orderReference: raw.orderReference ?? null,
    tokensPurchased: raw.tokensPurchased ?? null,
  }
}

const toDeposit = (
  raw: TransactionDetailWireDto['depositDetails'],
): TransactionDepositDetails | null => {
  if (!raw || typeof raw !== 'object') return null
  return {
    paymentMethod: raw.paymentMethod ?? null,
    bankName: raw.bankName ?? null,
    maskedAccount: raw.maskedAccount ?? null,
    referenceId: raw.referenceId ?? null,
  }
}

const toRental = (
  raw: TransactionDetailWireDto['rentalDetails'],
): TransactionRentalDetails | null => {
  if (!raw || typeof raw !== 'object') return null
  return {
    propertyName: raw.propertyName ?? null,
    referenceId: raw.referenceId ?? null,
  }
}

export function parseTransactionDetail(resp: unknown): TransactionDetail {
  const data = (get(resp, 'data') ?? {}) as TransactionDetailWireDto & {
    referenceId?: string | null
    propertyName?: string | null
  }

  return {
    id: data.id ?? '',
    displayTitle: data.displayTitle ?? null,
    amount: data.amount ?? null,
    currencyCode: data.currencyCode ?? null,
    transactionType: data.transactionType ?? null,
    status: data.status ?? null,
    balanceBefore: data.balanceBefore ?? null,
    balanceAfter: data.balanceAfter ?? null,
    transactionReason: data.transactionReason ?? null,
    createdAt: data.createdAt ?? null,
    referenceId: data.referenceId ?? null,
    propertyName: data.propertyName ?? null,
    withdrawalDetails: toWithdrawal(data.withdrawalDetails),
    investmentDetails: toInvestment(data.investmentDetails),
    depositDetails: toDeposit(data.depositDetails),
    rentalDetails: toRental(data.rentalDetails),
  }
}

// ── Filter-options parser ────────────────────────────────────────────────

export interface TransactionFilterOption {
  value: string
  label: string
}

export interface TransactionsFiltersDto {
  types: TransactionFilterOption[]
  statuses: TransactionFilterOption[]
  dateRanges: TransactionFilterOption[]
  sortOptions: TransactionFilterOption[]
}

export function parseTransactionsFilters(resp: unknown): TransactionsFiltersDto {
  return {
    types: (get(resp, 'data.types') ?? []) as TransactionFilterOption[],
    statuses: (get(resp, 'data.statuses') ?? []) as TransactionFilterOption[],
    dateRanges: (get(resp, 'data.dateRanges') ?? []) as TransactionFilterOption[],
    sortOptions: (get(resp, 'data.sortOptions') ?? []) as TransactionFilterOption[],
  }
}
